import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';

export interface BaseResponsiveImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'alt'
> {
  src: string;
  srcSet?: string;
  alt: string;
  sizes?: string;
  containerSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  draggable?: boolean | 'true' | 'false';
  /**
   * Optional overlay rendered inside the image container.
   * Uses the container's `position: relative` for absolute positioning.
   * Intended for AntiCaptureOverlay — no wrapper div needed.
   */
  overlay?: React.ReactNode;
}

/**
 * BaseResponsiveImage Component
 *
 * Renders a responsive, aspect-ratio preserving image that fits
 * inside any container using MUI's Box and img styling.
 * Includes a protective overlay, nesting, and anti-screenshot strategies.
 */
const BaseResponsiveImage: React.FC<BaseResponsiveImageProps> = React.memo(
  ({
    src,
    srcSet,
    alt = '',
    loading = 'lazy',
    sizes,
    containerSx = {},
    sx = {},
    onClick,
    onError,
    onLoad,
    draggable = false,
    overlay,
    ...imgProps
  }) => {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          maxHeight: '100vh',
          maxWidth: '100vw',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',

          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          WebkitPrintColorAdjust: 'exact',

          // Hide on print
          '@media print': {
            display: 'none',
          },

          '&::after': {
            content: '""',
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            visibility: 'hidden',
            '@media screen and (min-width: 0px)': {
              visibility: 'visible',
            },
          },
          ...containerSx,
        }}
      >
        {/* Overlay to block right-click and element selection */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            backgroundColor: 'transparent',
            pointerEvents: 'auto',
          }}
          onContextMenu={(e) => e.preventDefault()}
        />

        {/* Nested wrapper around the image */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            inset: 0,
            justifyContent: 'center',
            maxHeight: '100%',
            maxWidth: '100%',
            pointerEvents: 'none',
            position: 'absolute',
            width: '100%',
            zIndex: 1,
          }}
        >
          <Box
            alt={alt}
            component="img"
            draggable={draggable}
            loading={loading}
            onClick={onClick}
            onError={onError}
            onLoad={onLoad}
            sizes={sizes}
            src={src}
            srcSet={srcSet}
            {...imgProps}
            sx={{
              height: 'auto',
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'cover',
              pointerEvents: 'none',
              width: 'auto',
              ...sx,
            }}
          />
        </Box>

        {/* Optional overlay (e.g. AntiCaptureOverlay) — renders inside
            this container's position: relative context at z-index 3+ */}
        {overlay}
      </Box>
    );
  },
);

export default BaseResponsiveImage;
